import React from "react";
import { Box, Grid, GridItem, Progress, Text, Flex } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

type Step = { id: string; label: string };

type Props = {
  title?: string;
  steps: Step[];
  currentIndex: number; // 0..steps.length-1
  message?: string;
  isRequired?: boolean;
  isCancelled?: boolean;
  isCompleted?: boolean;
};

export function OrderHistoryProgressStepper({
  title = "진행 상황",
  steps,
  currentIndex,
  message = "공급사가 검토 중입니다.",
  isRequired,
  isCancelled,
  isCompleted,
}: Props) {
  const n = steps.length;
  const safeN = Math.max(n, 1);

  const maxIndex = safeN - 1;
  const idx = Math.min(Math.max(currentIndex, 0), maxIndex);

  // Progress value 계산은 여전히 필요합니다(막대 채움용).
  // 하지만 "문구 위치"는 퍼센트가 아니라 Grid 컬럼으로 잡습니다.
  const denom = Math.max(safeN - 1, 1);
  const progressValue = (idx / denom) * 100;

  // Grid 컬럼 수: 2n - 1 (n=1이면 1)
  const cols = Math.max(2 * safeN - 1, 1);
  const makeTemplate = (n: number) => {
    const template: string[] = [];
    for (let i = 0; i < n; i += 1) {
      template.push(i % 2 === 0 ? "auto" : "1fr");
    }
    return template.join(" ");
  };
  // const template = `repeat(${cols}, 1fr)`;
  const template = makeTemplate(cols);

  // Chakra Grid의 colStart/colEnd는 1부터 시작
  const stepCol = (i: number) => 2 * i + 1; // 1,3,5,...

  const getMsgColor = () => {
    if (isRequired) return "#CC1D1D";
    if (isCancelled) return "#444444";
    if (isCompleted) return "#4C994F";
    return "#1272EF";
  };

  const { isKor } = useI18nHelper();

  return (
    <Flex
      boxShadow={`0 0 0 0.5px ${
        isCancelled ? "#8F8F8C" : isCompleted ? "#16B41C" : "#1272EF"
      }`}
      borderRadius="6px"
      p={"16px"}
      gap="16px"
      align="center"
      background={isCancelled ? "#EFEFED" : isCompleted ? "#F0FAEF" : "#F0F9FF"}
    >
      <Box flex="1">
        {/* Row 1: 메시지 (Grid로 스냅) */}
        <Grid templateColumns={template} minH="24px" alignItems="center">
          <GridItem colStart={1} colEnd={2}>
            <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
              {title}
            </Text>
          </GridItem>
          {message && (
            <GridItem colStart={2} colEnd={3} justifySelf="flex-start">
              <Text
                textStyle={"body-md-bold"}
                color={getMsgColor()}
                textIndent={"60px"}
                whiteSpace={"nowrap"}
              >
                {message}
              </Text>
            </GridItem>
          )}
        </Grid>

        <Box position={"relative"}>
          {/* Row 2: Progress + 점 (같은 Grid 위에 겹치기) */}
          <Grid
            templateColumns={template}
            alignItems="center"
            mt="8px"
            mb="10px"
          >
            {/* Progress는 전체 span */}
            <GridItem
              colStart={1}
              colEnd={cols + 1}
              zIndex={0}
              position={"relative"}
            >
              <Progress
                value={progressValue}
                height="8px"
                borderRadius="999px"
                bg={
                  isCancelled ? "#D4D4D1" : isCompleted ? "#4C994F" : "#D4D4D1"
                }
                colorScheme="blue"
              />
            </GridItem>

            <Grid
              templateColumns={template}
              position={"absolute"}
              width={"100%"}
            >
              {/* 점들은 단계 컬럼에 스냅 */}
              {steps.map((s, i) => {
                const reached = i <= idx;
                return (
                  <GridItem
                    key={s.id}
                    colStart={stepCol(i)}
                    colEnd={stepCol(i) + 1}
                    transform={`translate(${
                      i === steps.length - 1 ? "5px" : "-6px"
                    }, 0.5px)`}
                  >
                    <CustomIcon
                      src={"dot"}
                      color={"#FFFFFF"}
                      width={24}
                      height={24}
                    />
                  </GridItem>
                );
              })}
            </Grid>
          </Grid>
        </Box>

        {/* Row 3: 라벨 (점과 동일 컬럼에 스냅) */}
        <Grid templateColumns={template} alignItems="start">
          {steps.map((s, i) => {
            const isCurrentStep = idx !== 0 && Math.floor(idx) === i;
            const isBeforeStep = idx < i;
            const translateMap: Record<number, string> = {
              1: "-35%",
              2: "-45%",
              3: isKor ? "-35%" : "-75%",
              4: isKor ? "0%" : "-25%",
            };

            return (
              <GridItem
                key={s.id}
                colStart={stepCol(i)}
                colEnd={stepCol(i) + 1}
                transform={`translateX(${translateMap[i] ?? "0px"})`}
              >
                <Text
                  textAlign="center"
                  textStyle={isCurrentStep ? "body-md-bold" : "body-md-regular"}
                  color={
                    isCancelled
                      ? "#C0C0C0"
                      : isCurrentStep
                      ? "#363636"
                      : isBeforeStep
                      ? "#444444"
                      : "#6F6F6C"
                  }
                >
                  {s.label}
                </Text>
              </GridItem>
            );
          })}
        </Grid>
      </Box>
    </Flex>
  );
}
